// Reusable iOS-style mobile components.
// All visible ONLY on mobile (the parent renders them inside `lg:hidden` wrappers).

import { ChevronRight, Search } from "lucide-react";

export const IOS_BG = "bg-[#F2F2F7]";
export const IOS_TEXT = "text-[#1C1C1E]";
export const IOS_GRAY = "text-[#8E8E93]";
export const IOS_CHEVRON = "text-[#C7C7CC]";
export const IOS_ACCENT = "#E07A1F";

export function LargeTitle({ eyebrow, title, right }) {
  return (
    <div className="px-5 pt-1 pb-4 flex items-end justify-between gap-3">
      <div className="min-w-0">
        {eyebrow && (
          <div className="text-[13px] text-[#8E8E93] font-medium">{eyebrow}</div>
        )}
        <h1 className="text-[34px] font-bold tracking-tight leading-tight text-[#1C1C1E] truncate">
          {title}
        </h1>
      </div>
      {right && <div className="flex items-center gap-2 shrink-0 pb-1">{right}</div>}
    </div>
  );
}

export function SegmentedControl({ value, onChange, options }) {
  return (
    <div className="flex bg-[#E9E9EB] rounded-xl p-0.5 text-[13px] font-semibold">
      {options.map((opt) => {
        const isActive = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            data-testid={`segmented-${opt.value}`}
            className={`flex-1 text-center py-1.5 rounded-[10px] transition ${
              isActive ? "bg-white text-[#1C1C1E]" : "text-[#8E8E93]"
            }`}
            style={isActive ? { boxShadow: "0 1px 3px rgba(0,0,0,0.08)" } : {}}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function IosSearch({ value, onChange, placeholder = "Search" }) {
  return (
    <div className="flex items-center gap-2 bg-[#E9E9EB] rounded-xl px-3 py-2.5">
      <Search size={16} className="text-[#8E8E93] shrink-0" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-transparent outline-none text-[15px] flex-1 placeholder:text-[#8E8E93]"
        data-testid="ios-search-input"
      />
    </div>
  );
}

export function Group({ title, children, className = "" }) {
  return (
    <div className={`mt-6 ${className}`}>
      {title && (
        <div className="px-5 pb-2 text-[12px] uppercase tracking-wider text-[#8E8E93] font-semibold">
          {title}
        </div>
      )}
      <div
        className="mx-4 bg-white rounded-2xl overflow-hidden border border-black/[0.04]"
        style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
      >
        {children}
      </div>
    </div>
  );
}

export function Row({ icon, label, value, sub, chevron = true, tint, last, onClick, "data-testid": testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left flex items-center gap-3 px-4 py-3.5 active:bg-black/[0.04] transition ${
        !last ? "border-b border-black/[0.05]" : ""
      }`}
    >
      {icon && (
        <div
          className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
          style={{ background: tint || "#F2F2F7", color: "#1C1C1E" }}
        >
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-medium leading-tight truncate text-[#1C1C1E]">{label}</div>
        {sub && <div className="text-[13px] text-[#8E8E93] mt-0.5 truncate">{sub}</div>}
      </div>
      {value && <div className="text-[15px] font-semibold text-[#1C1C1E]">{value}</div>}
      {chevron && <ChevronRight size={18} className="text-[#C7C7CC] shrink-0" />}
    </button>
  );
}

export function KpiTile({ icon, label, value, delta, tint, iconColor, alert, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white rounded-2xl p-4 border border-black/[0.04]"
      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
    >
      <div
        className="h-9 w-9 rounded-xl grid place-items-center mb-3"
        style={{ background: tint, color: iconColor }}
      >
        {icon}
      </div>
      <div className="text-[12px] text-[#8E8E93] font-medium uppercase tracking-wider">{label}</div>
      <div className={`text-[30px] font-bold tracking-tight leading-none mt-1 ${alert ? "text-[#D93B4B]" : "text-[#1C1C1E]"}`}>
        {value}
      </div>
      {delta && <div className="text-[12px] text-[#8E8E93] mt-1.5">{delta}</div>}
    </div>
  );
}
